import { useEffect, useState } from "react"
import { request } from "@data/datoRequest"
import { allUpholsteryCarsQuery } from "../../graphql"
import { useUpholsteryDataStore } from "../../store/store"
import { Car } from "../../types"
import {
  isPdmStructureV2Enabled,
  usePdmVehicleCatalogStore,
} from "@views/UpholsteryV2"

/**
 * Helper function to convert a string to slug format
 */
function toSlug(str: string): string {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
}

/**
 * Hook to fetch upholstery cars and store them in the Zustand store
 * Can be called on any page to preload upholstery car data
 */
export function useFetchUpholsteryCars() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const { cars, actions } = useUpholsteryDataStore()
  const pickerCars = usePdmVehicleCatalogStore((s) => s.pickerCars)
  const pdmCatalogLoading = usePdmVehicleCatalogStore(
    (s) => s.loading && s.pickerCars.length === 0
  )

  useEffect(() => {
    // PDM V2: catalog loading is owned by `useGlobalVehicleCatalogLoader` in
    // `Layout`. This hook must not trigger a PDM fetch; it only reads the store.
    if (isPdmStructureV2Enabled()) {
      return
    }

    // Only fetch if cars are not already loaded
    if (cars.length > 0) {
      return
    }

    const fetchCars = async () => {
      setLoading(true)
      setError(null)
      try {
        const data = await request({
          query: allUpholsteryCarsQuery,
        })

        const upholsteryCars: Car[] = (data.allUpholsteryCars || []).map(
          (car: any) => ({
            id: car.id,
            make: car.make,
            model: car.model,
            years: car.years,
            trims: car.trims || "",
            driverFactorySeatType: car.driverFactorySeatType || "",
            passengerFactorySeatType: car.passengerFactorySeatType || "",
            image: car.image || null,
          })
        )

        actions.setCars(upholsteryCars)
      } catch (err) {
        console.error("Error fetching upholstery cars:", err)
        setError(err instanceof Error ? err : new Error("Unknown error"))
      } finally {
        setLoading(false)
      }
    }

    fetchCars()
  }, [cars.length, actions])

  if (isPdmStructureV2Enabled()) {
    return { loading: pdmCatalogLoading, error, cars: pickerCars }
  }

  return { loading, error, cars }
}

/**
 * Helper function to transform upholstery cars to CarSelector format
 * Converts flat structure (make: string, model: string) to nested structure
 * (make: { name: string, slug: string }, model: { name: string, slug: string })
 * Note: Upholstery cars don't have trim data in the same format, so trim is optional
 */
export function transformUpholsteryCarsForCarSelector(
  upholsteryCars: Car[]
): Array<{
  make: { name: string; slug: string }
  model: { name: string; slug: string }
  years: string
  module: string
  trim?: { name: string; slug: string }
}> {
  return upholsteryCars.map((car) => {
    const baseCar = {
      make: {
        name: car.make,
        slug: toSlug(car.make),
      },
      model: {
        name: car.model,
        slug: toSlug(car.model),
      },
      years: car.years,
      module: "automotive", // Upholstery cars are automotive
    }

    // Upholstery cars have trims as a comma-separated string, but CarSelector
    // expects trim objects. For now, we'll leave trim undefined since the
    // CarSelector code handles optional trim gracefully.
    return baseCar
  })
}
